import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Dice1, 
  Dice2, 
  Dice3, 
  Dice4, 
  Dice5, 
  Dice6,
  Plus,
  Trash2,
  RotateCcw,
  Play,
  CheckCircle,
  AlertTriangle,
  Target,
  Users,
  MapPin,
  Activity,
  Database,
  Clock,
  User,
  Users2,
  Settings,
  Lightbulb,
  BarChart3
} from 'lucide-react';

// 3D Shape Definitions
const shapeTypes = [
  {
    id: 'tetrahedron',
    name: 'Tetrahedron',
    faces: 4,
    icon: '△',
    description: '4 faces - Perfect for basic categories',
    color: 'blue'
  },
  {
    id: 'cube',
    name: 'Cube',
    faces: 6,
    icon: '⬜',
    description: '6 faces - Great for comprehensive analysis',
    color: 'green'
  },
  {
    id: 'octahedron',
    name: 'Octahedron',
    faces: 8,
    icon: '⬟',
    description: '8 faces - Detailed categorization',
    color: 'purple'
  },
  {
    id: 'dodecahedron',
    name: 'Dodecahedron',
    faces: 12,
    icon: '⬢',
    description: '12 faces - Complex multi-dimensional analysis',
    color: 'orange'
  },
  {
    id: 'icosahedron',
    name: 'Icosahedron',
    faces: 20,
    icon: '⬡',
    description: '20 faces - Comprehensive coverage',
    color: 'red'
  }
];

// Predefined Face Sets
const predefinedFaceSets = {
  // System Analysis
  systemAnalysis: {
    name: 'System Analysis',
    description: 'Comprehensive system breakdown',
    faces: [
      { id: 'users', label: 'Users', icon: Users, category: 'stakeholder' },
      { id: 'components', label: 'Components', icon: Settings, category: 'technical' },
      { id: 'interactions', label: 'Interactions', icon: Activity, category: 'behavior' },
      { id: 'data', label: 'Data', icon: Database, category: 'information' },
      { id: 'processes', label: 'Processes', icon: BarChart3, category: 'workflow' },
      { id: 'context', label: 'Context', icon: MapPin, category: 'environment' }
    ]
  },
  
  // Prioritization Framework
  prioritization: {
    name: 'Prioritization Framework',
    description: 'What to prioritize in your work',
    faces: [
      { id: 'people', label: 'People', icon: Users, category: 'stakeholder' },
      { id: 'places', label: 'Places', icon: MapPin, category: 'location' },
      { id: 'activities', label: 'Activities', icon: Activity, category: 'action' },
      { id: 'information', label: 'Information', icon: Database, category: 'data' },
      { id: 'time', label: 'Time', icon: Clock, category: 'temporal' }
    ]
  },
  
  // Decision Scope
  decisionScope: {
    name: 'Decision Scope',
    description: 'Scope of decision-making',
    faces: [
      { id: 'individual', label: 'Individual', icon: User, category: 'personal' },
      { id: 'group', label: 'Group', icon: Users2, category: 'collective' }
    ]
  },
  
  // Risk Assessment
  riskAssessment: {
    name: 'Risk Assessment',
    description: 'Risk evaluation dimensions',
    faces: [
      { id: 'probability', label: 'Probability', icon: BarChart3, category: 'likelihood' },
      { id: 'impact', label: 'Impact', icon: Target, category: 'consequence' },
      { id: 'detectability', label: 'Detectability', icon: AlertTriangle, category: 'visibility' },
      { id: 'mitigation', label: 'Mitigation', icon: CheckCircle, category: 'control' }
    ]
  },
  
  // Learning Dimensions
  learningDimensions: {
    name: 'Learning Dimensions',
    description: 'Multi-dimensional learning approach',
    faces: [
      { id: 'knowledge', label: 'Knowledge', icon: Lightbulb, category: 'cognitive' },
      { id: 'skills', label: 'Skills', icon: Activity, category: 'behavioral' },
      { id: 'attitudes', label: 'Attitudes', icon: Users, category: 'emotional' },
      { id: 'behaviors', label: 'Behaviors', icon: BarChart3, category: 'action' }
    ]
  }
};

// Dice Icons
const diceIcons = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6];

export const ShapeCombinator: React.FC = () => {
  const [shapes, setShapes] = useState<any[]>([]);
  const [selectedPredefined, setSelectedPredefined] = useState<string>('');
  const [combinations, setCombinations] = useState<any[]>([]);
  const [isRolling, setIsRolling] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Add a new shape
  const addShape = (shapeType: any, faceSet?: any) => {
    const newShape = {
      id: `shape-${Date.now()}`,
      type: shapeType,
      faces: faceSet ? faceSet.faces : Array.from({ length: shapeType.faces }, (_, i) => ({
        id: `face-${i}`,
        label: `Option ${i + 1}`,
        icon: diceIcons[i % diceIcons.length],
        category: 'custom'
      })),
      name: faceSet ? faceSet.name : `${shapeType.name} ${shapes.length + 1}`,
      description: faceSet ? faceSet.description : `Custom ${shapeType.name.toLowerCase()}`
    };
    setShapes(prev => [...prev, newShape]);
  };

  // Remove a shape
  const removeShape = (shapeId: string) => {
    setShapes(prev => prev.filter(shape => shape.id !== shapeId));
  };

  // Update face label
  const updateFaceLabel = (shapeId: string, faceId: string, newLabel: string) => {
    setShapes(prev => prev.map(shape => 
      shape.id === shapeId 
        ? {
            ...shape,
            faces: shape.faces.map((face: any) => 
              face.id === faceId ? { ...face, label: newLabel } : face
            )
          }
        : shape
    ));
  };

  // Generate all possible combinations
  const generateCombinations = () => {
    if (shapes.length === 0) return [];

    const combinations: any[] = [];
    
    // Generate cartesian product of all shape faces
    const generateProduct = (shapeIndex: number, currentCombination: any[]) => {
      if (shapeIndex === shapes.length) {
        combinations.push([...currentCombination]);
        return;
      }

      const currentShape = shapes[shapeIndex];
      currentShape.faces.forEach((face: any) => {
        generateProduct(shapeIndex + 1, [...currentCombination, { shape: currentShape, face }]);
      });
    };

    generateProduct(0, []);
    return combinations;
  };

  // Roll dice (generate random combination)
  const rollDice = () => {
    if (shapes.length === 0) return;
    
    setIsRolling(true);
    setShowResults(false);
    
    setTimeout(() => {
      const randomCombination = shapes.map(shape => {
        const randomFace = shape.faces[Math.floor(Math.random() * shape.faces.length)];
        return { shape, face: randomFace };
      });
      
      setCombinations([randomCombination]);
      setShowResults(true);
      setIsRolling(false);
    }, 1500);
  };

  // Test all combinations
  const testAllCombinations = () => {
    const allCombinations = generateCombinations();
    setCombinations(allCombinations);
    setShowResults(true);
  };

  // Calculate total combinations
  const totalCombinations = useMemo(() => {
    return shapes.reduce((total, shape) => total * shape.faces.length, 1);
  }, [shapes]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/lab" className="inline-flex items-center gap-2 text-[var(--mustard)] hover:text-[var(--mustard)] mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to The Lab
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-[var(--mustard-dim)] text-[var(--mustard)]">
            The Lab
          </div>
          <Dice1 className="h-10 w-10 text-[var(--mustard)]" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">3D Shape Combinator</h1>
            <p className="text-charcoal-600">Test all possible logical combinations</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          Use 3D shapes with different numbers of faces to ensure comprehensive coverage. 
          Each face represents an option, and you can test all possible combinations or roll randomly.
        </p>
      </div>

      {/* Shape Library */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-4">Shape Library</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {shapeTypes.map((shape) => (
            <button
              key={shape.id}
              onClick={() => addShape(shape)}
              className="p-4 border-2 border-[var(--rule)] hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] text-center transition-all"
            >
              <div className="text-4xl mb-2">{shape.icon}</div>
              <h3 className="font-semibold text-charcoal-900">{shape.name}</h3>
              <p className="text-xs text-charcoal-600">{shape.faces} faces</p>
            </button>
          ))}
        </div>

        {/* Predefined Face Sets */}
        <div className="border-t border-[var(--rule)] pt-6">
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Predefined Face Sets</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(predefinedFaceSets).map(([key, faceSet]) => (
              <button
                key={key}
                onClick={() => {
                  const shapeType = shapeTypes.find(s => s.faces === faceSet.faces.length) || shapeTypes[1]; // Default to cube
                  addShape(shapeType, faceSet);
                }}
                className="p-4 border-2 border-[var(--rule)] hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] text-left transition-all"
              >
                <h4 className="font-semibold text-charcoal-900 mb-2">{faceSet.name}</h4>
                <p className="text-sm text-charcoal-600 mb-3">{faceSet.description}</p>
                <div className="flex flex-wrap gap-1">
                  {faceSet.faces.slice(0, 3).map((face, idx) => {
                    const Icon = face.icon;
                    return (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--hover-tint)] text-xs rounded">
                        <Icon className="h-3 w-3" />
                        {face.label}
                      </span>
                    );
                  })}
                  {faceSet.faces.length > 3 && (
                    <span className="text-xs text-charcoal-500">+{faceSet.faces.length - 3} more</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Active Shapes */}
      {shapes.length > 0 && (
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-charcoal-900">
              Active Shapes ({shapes.length})
            </h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-charcoal-600">
                Total Combinations: <strong>{totalCombinations.toLocaleString()}</strong>
              </span>
              <button
                onClick={rollDice}
                disabled={isRolling}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50 transition-colors"
              >
                <RotateCcw className={`h-4 w-4 ${isRolling ? 'animate-spin' : ''}`} />
                Roll Dice
              </button>
              <button
                onClick={testAllCombinations}
                className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors"
              >
                <CheckCircle className="h-4 w-4" />
                Test All
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {shapes.map((shape) => (
              <div key={shape.id} className="border-2 border-[var(--rule)] p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{shape.type.icon}</div>
                    <div>
                      <h3 className="font-semibold text-charcoal-900">{shape.name}</h3>
                      <p className="text-sm text-charcoal-600">{shape.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeShape(shape.id)}
                    className="p-2 text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {shape.faces.map((face: any, index: number) => {
                    const Icon = face.icon;
                    return (
                      <div key={face.id} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-charcoal-500" />
                          <span className="text-xs text-charcoal-500">Face {index + 1}</span>
                        </div>
                        <input
                          type="text"
                          value={face.label}
                          onChange={(e) => updateFaceLabel(shape.id, face.id, e.target.value)}
                          className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          placeholder="Enter option"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {showResults && combinations.length > 0 && (
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-charcoal-900">
              {combinations.length === 1 ? 'Random Combination' : `All Combinations (${combinations.length})`}
            </h2>
            {combinations.length > 1 && (
              <div className="text-sm text-charcoal-600">
                Showing {Math.min(combinations.length, 10)} of {combinations.length} combinations
              </div>
            )}
          </div>

          <div className="space-y-4">
            {combinations.slice(0, 10).map((combination, index) => (
              <div key={index} className="border border-[var(--rule)] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs rounded-full">
                    Combination {index + 1}
                  </span>
                  {combinations.length === 1 && (
                    <span className="px-2 py-1 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs rounded-full">
                      Random Roll
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {combination.map((item: any, itemIndex: number) => {
                    const Icon = item.face.icon;
                    return (
                      <div key={itemIndex} className="flex items-center gap-3 p-3 bg-[var(--surface)]">
                        <div className="text-lg">{item.shape.type.icon}</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-charcoal-900">{item.shape.name}</div>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-charcoal-500" />
                            <span className="text-sm text-charcoal-600">{item.face.label}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {combinations.length > 10 && (
            <div className="mt-6 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <p className="text-sm text-[var(--mustard)]">
                <strong>Note:</strong> Showing first 10 combinations. Total: {combinations.length.toLocaleString()} combinations.
                Consider using "Roll Dice" for random sampling or reducing the number of shapes/faces for manageable results.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Usage Guidelines */}
      <div className="bg-sage-50 p-6 border border-sage-200">
        <h3 className="text-lg font-semibold text-sage-900 mb-3">Usage Guidelines</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-sage-800 mb-2">Coverage Testing</h4>
            <ul className="text-sm text-sage-700 space-y-1">
              <li>• Use "Test All" to ensure comprehensive coverage</li>
              <li>• Each combination represents a unique scenario</li>
              <li>• Verify you've considered all possible interactions</li>
              <li>• Use for risk assessment and gap analysis</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-sage-800 mb-2">Random Exploration</h4>
            <ul className="text-sm text-sage-700 space-y-1">
              <li>• Use "Roll Dice" for random scenario generation</li>
              <li>• Great for brainstorming and creative exploration</li>
              <li>• Helps discover unexpected combinations</li>
              <li>• Useful for stress testing and edge cases</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
